import { ReactNode } from 'react';

import { SxProps, Theme } from '@mui/material/styles';

import { Box } from '../box/box';
import { useTheme } from '../styles/styles';

export type StatusTone = 'success' | 'neutral' | 'danger' | 'info' | 'warning';

export const statusToneFor = (status: string): StatusTone => {
  switch (status.trim().toLowerCase()) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'neutral';
    case 'terminated':
      return 'danger';
    case 'pending':
      return 'warning';
    default:
      return 'info';
  }
};

const toneColors = (theme: Theme, tone: StatusTone) => {
  const c = theme.custom;
  switch (tone) {
    case 'success':
      return {
        text: c?.statusActiveText,
        dot: c?.listStatusActive,
        bg: c?.headerActiveBg,
      };
    case 'neutral':
      return {
        text: c?.shellSideIcon,
        dot: c?.statusInactiveDot,
        bg: c?.statusTagNeutralBg,
      };
    case 'danger':
      return {
        text: c?.messageDangerIcon,
        dot: c?.listStatusInactive,
        bg: c?.messageDangerBg,
      };
    case 'warning':
      return {
        text: c?.requestText,
        dot: c?.statusTagWarningDot,
        bg: c?.requestBg,
      };
    default:
      return {
        text: c?.brandIndigo,
        dot: c?.statusTagInfoDot,
        bg: c?.statusTagInfoBg,
      };
  }
};

export const StatusTag = ({
  tone,
  children,
  sx,
}: {
  tone: StatusTone;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) => {
  const theme = useTheme();
  const { text, dot, bg } = toneColors(theme, tone);
  return (
    <Box
      component="span"
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          height: 22,
          padding: '0 9px',
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          color: text,
          background: bg,
          '&::before': {
            content: '""',
            width: 6,
            height: 6,
            flexShrink: 0,
            borderRadius: '50%',
            background: dot,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Box>
  );
};
